import React, { useContext, useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../styles/ComponentStyles/Navbar.css";
import logoWhite from "../../assets/images/LogoCombatSolutionsWhite.png";
import logoBlack from "../../assets/images/LogoCombatSolutionsBlack.png";

import { ModalContext } from "../../context/ModalContext";
import { trackEvent } from "../../utils/google-analytics";


const Navbar = () => {
  const { openStrategy } = useContext(ModalContext);
  const location = useLocation();
  const currentPage = location.pathname;

  const [whoDropdownOpen, setWhoDropdownOpen] = useState(false);
  const [whatDropdownOpen, setWhatDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [logoColor, setLogoColor] = useState("white");
  const [isLogoReady, setIsLogoReady] = useState(false);
const [defenceNestedOpen, setDefenceNestedOpen] = useState(false);
const defenceNestedRef = useRef(null);


  const whoRef = useRef(null);
  const whatRef = useRef(null);
  const navbarRef = useRef(null);
  const logoCheckInterval = useRef(null);
  const observerRef = useRef(null);


  const allowedPaths = ["/", "/career", "/about-us","/contact","/services","/technologies"];
  // here i added defence paths also
  const privacyPolicyPaths = ["/Privacy", "/Terms", "/defence-products", "/defence-support", "/defence-overview", "/defence-expertise"];
  const isPrivacyPolicyPage = privacyPolicyPaths.includes(location.pathname);

  // Function to detect background brightness
  const detectBackgroundBrightness = () => {
    if (!navbarRef.current || typeof window === "undefined") return "white";

    const navbar = navbarRef.current;
    const navbarRect = navbar.getBoundingClientRect();
    
    // Get the background elements behind the navbar
    const elementsBelowNavbar = [];
    
    // Check for fixed/absolute positioned backgrounds
    const potentialBackgrounds = document.querySelectorAll(
      'div, section, header, main, .hero, .background, .section-background'
    );
    
    // Create a point in the middle of the navbar to sample
    const samplePoints = [
      { x: navbarRect.left + navbarRect.width / 2, y: navbarRect.top + navbarRect.height / 2 },
      { x: navbarRect.left + 20, y: navbarRect.top + navbarRect.height / 2 },
      { x: navbarRect.right - 20, y: navbarRect.top + navbarRect.height / 2 },
    ];
    
    let darkestPoint = 255; // 0-255, lower = darker
    let lightestPoint = 0; // 0-255, higher = lighter
    
    samplePoints.forEach(point => {
      // Get the element at this point
      const elementAtPoint = document.elementFromPoint(point.x, point.y);
      if (elementAtPoint && elementAtPoint !== navbar) {
        const color = getElementBackgroundColor(elementAtPoint, point.x, point.y);
        if (color) {
          const brightness = calculateBrightness(color);
          darkestPoint = Math.min(darkestPoint, brightness);
          lightestPoint = Math.max(lightestPoint, brightness);
        }
      }
    });
    
    // If we couldn't detect any colors, default based on scrolled state
    if (darkestPoint === 255 && lightestPoint === 0) {
      return isScrolled ? "black" : "white";
    }
    
    // Use the average brightness of detected points
    const avgBrightness = (darkestPoint + lightestPoint) / 2;
    
    // Threshold: below 128 = dark background, use white logo
    // above 128 = light background, use black logo
    return avgBrightness < 128 ? "white" : "black";
  };

  // Helper to get computed background color of an element
  const getElementBackgroundColor = (element, x, y) => {
    if (!element) return null;
    
    const computedStyle = window.getComputedStyle(element);
    let bgColor = computedStyle.backgroundColor;
    
    // If background is transparent or rgba with low alpha, check parent
    if (bgColor === 'transparent' || 
        bgColor === 'rgba(0, 0, 0, 0)' ||
        (bgColor.includes('rgba') && parseFloat(bgColor.split(',')[3]) < 0.5)) {
      
      // Try to get background image
      const bgImage = computedStyle.backgroundImage;
      if (bgImage && bgImage !== 'none') {
        // For images, we need to approximate
        // This is simplified - in production you might want to analyze images
        return 'rgb(255, 255, 255)'; // Default light for images
      }
      
      // Check parent element
      if (element.parentElement) {
        return getElementBackgroundColor(element.parentElement, x, y);
      }
      
      return null;
    }
    
    return bgColor;
  };

  // Calculate brightness from RGB color
  const calculateBrightness = (color) => {
    if (!color) return 255;
    
    // Parse RGB values
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      
      // Calculate perceived brightness (weighted average)
      return (r * 299 + g * 587 + b * 114) / 1000;
    }
    
    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return (r * 299 + g * 587 + b * 114) / 1000;
    }
    
    return 255; // Default to light
  };

  // Update logo color based on background
  const updateLogoColor = () => {
    const isMobile = () => window.innerWidth <= 768;

    if (isPrivacyPolicyPage) {
      setLogoColor("black");
      return;
    }
    
    // Don't apply dynamic logo on allowed paths when not scrolled
    if (!allowedPaths.includes(location.pathname)) {
      setLogoColor("white");
      return;
    }
    
    if (isMobile()) {
    setLogoColor(isScrolled ? "black" : "white");
    return;
    }

    // When not scrolled (at top of page), use white logo by default
    if (!isScrolled) {
      setLogoColor("white");
      return;
    }
    
    // When scrolled, detect background brightness
    const newLogoColor = detectBackgroundBrightness();
    setLogoColor(newLogoColor);
  };

  // Handle scroll effect and logo update
  useEffect(() => {
    if (isPrivacyPolicyPage) {
      setIsScrolled(false);
      setLogoColor("black");
      return;
    }

    if (!allowedPaths.includes(location.pathname)) {
      setIsScrolled(false);
      setLogoColor("white");
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const wasScrolled = isScrolled;
      const nowScrolled = currentScrollY > viewportHeight;
      
      setIsScrolled(nowScrolled);
      
      // Only update logo when scrolled state changes or when scrolling
      if (wasScrolled !== nowScrolled || nowScrolled) {
        // Debounce the logo update
        clearTimeout(logoCheckInterval.current);
        logoCheckInterval.current = setTimeout(updateLogoColor, 50);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    
    // Initial check
    updateLogoColor();
    
    // Set up MutationObserver to detect background changes
    observerRef.current = new MutationObserver(() => {
      if (isScrolled) {
        updateLogoColor();
      }
    });
    
    observerRef.current.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: true,
      childList: true
    });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(logoCheckInterval.current);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [location.pathname, isPrivacyPolicyPage, isScrolled]);

  // Preload both logos to prevent flicker
  useEffect(() => {
    const preloadLogo = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = resolve;
      });
    };
    
    Promise.all([preloadLogo(logoWhite), preloadLogo(logoBlack)])
      .then(() => {
        setIsLogoReady(true);
      });
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (whoRef.current && !whoRef.current.contains(e.target)) {
        setWhoDropdownOpen(false);
      }
      if (whatRef.current && !whatRef.current.contains(e.target)) {
        setWhatDropdownOpen(false);
      }

      if (
      defenceNestedRef.current &&
      !defenceNestedRef.current.contains(e.target) &&
      whatRef.current &&
      !whatRef.current.contains(e.target)
    ) {
      setDefenceNestedOpen(false);
    }
    
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleStrategyCallClick = () => {
    setMobileMenuOpen(false);
    openStrategy();
  };

  const getNavbarClasses = () => {
    let classes = "navbar";
    
    if (isPrivacyPolicyPage) {
      classes += " privacy-policy-nav";
    } else if (isScrolled) {
      classes += " scrolled";
    }
    
    return classes;
  };

  const getLinkClass = (isDropdownButton = false) => {
    if (isPrivacyPolicyPage) {
      return isDropdownButton ? "privacy-nav-link-btn" : "";
    }
    return "";
  };

  // Get the appropriate logo based on detected background
  const getLogo = () => {
    if (!isLogoReady) {
      // Return a placeholder or default while logos load
      return logoWhite;
    }
    
    return logoColor === "black" ? logoBlack : logoWhite;
  };

  return (
    <>
      <div className="nav-holder">
        <nav className={getNavbarClasses()} ref={navbarRef}>
          {/* LOGO */}
          <Link to="/" className="logo"
            onClick={() =>
              trackEvent("nav_logo_click", {
                page: currentPage,
                location: "Navbar",
              })
            }>
            <div className="nav-logo-icon">
              <img 
                src={getLogo()} 
                alt="Combat Solutions Logo" 
                style={{ 
                  opacity: isLogoReady ? 1 : 0,
                  transition: 'opacity 0.2s ease-in-out'
                }}
              />
            </div>
            <span>Combat Solutions AI</span>
          </Link>

          {/* NAV LINKS */}
          <ul className={`nav-links ${mobileMenuOpen ? "active" : ""}`}>
            {/* WHO WE ARE DROPDOWN */}
            <li className="dropdown-wrapper" ref={whoRef}>
              <button
                className={`nav-link-btn ${whoDropdownOpen ? "active" : ""} ${getLinkClass(true)} ${logoColor === "black" ? "text-dark" : ""}`}
                onClick={() => {
                  setWhoDropdownOpen(!whoDropdownOpen);
                  setWhatDropdownOpen(false);
                  trackEvent("nav_dropdown_open", {
                    page: currentPage,
                    menu: "Who We Are",
                  });
                }}
                type="button"
              >
                Who We Are
                <span className="dropdown-icon">
                  <i className="bi bi-chevron-down" />
                </span>
              </button>

              <div className={`dropdown-menu ${whoDropdownOpen ? "active" : ""}`}>
                <Link
                  to="/about-us"
                  className={isActive("/about-us") ? "active" : ""}
                  onClick={() => {
                    trackEvent("nav_link_click", {
                      page: currentPage,
                      link: "About Us",
                    });
                    setWhoDropdownOpen(false);
                    handleMobileMenuClose();
                  }}
                >
                  About Us
                </Link>

                <Link
                  to="/career"
                  className={isActive("/career") ? "active" : ""}
                  onClick={() => {
                    setWhoDropdownOpen(false);
                    handleMobileMenuClose();
                  }}
                >
                  Career
                </Link>

                <Link
                  to="/contact"
                  className={isActive("/contact") ? "active" : ""}
                  onClick={() => {
                    setWhoDropdownOpen(false);
                    handleMobileMenuClose();
                  }}
                >
                  Contact Us
                </Link>
              </div>
            </li>

            {/* WHAT WE DO DROPDOWN */}
            <li className="dropdown-wrapper" ref={whatRef}>
              <button
                className={`nav-link-btn ${whatDropdownOpen ? "active" : ""} ${getLinkClass(true)} ${logoColor === "black" ? "text-dark" : ""}`}
                onClick={() => {
                  setWhatDropdownOpen(!whatDropdownOpen);
                  setWhoDropdownOpen(false);
                  trackEvent("nav_dropdown_open", {
                    page: currentPage,
                    menu: "What We Do",
                  });
                }}
                type="button"
              >
                What We Do
                <span className="dropdown-icon">
                  <i className="bi bi-chevron-down" />
                </span>
              </button>

             <div className={`dropdown-menu ${whatDropdownOpen ? "active" : ""}`}>

  <Link to="/services" onClick={() => {
    setWhatDropdownOpen(false);
    handleMobileMenuClose();
  }}>
    Services
  </Link>

  <Link to="/products" onClick={() => {
    setWhatDropdownOpen(false);
    handleMobileMenuClose();
  }}>
    Products
  </Link>

  <Link to="/technologies" onClick={() => {
    setWhatDropdownOpen(false);
    handleMobileMenuClose();
  }}>
    Technologies
  </Link>

  {/* DEFENCE – NORMAL ITEM, NO ARROW */}
<button
  className={`dropdown-defence-trigger ${defenceNestedOpen ? "active" : ""}`}
  type="button"
  onClick={(e) => {
    e.stopPropagation();           // 🔥 block parent & outside click
    setDefenceNestedOpen(prev => !prev);   // true toggle
  }}
>
  <span>Defence & Security AI</span>
  <span className="defence-arrow">
    <i className="bi bi-chevron-right" />
  </span>
</button>


  {/* NESTED PANEL */}
  <div
    ref={defenceNestedRef}
    className={`dropdown-menu nested-dropdown ${defenceNestedOpen ? "active" : ""}`}
     onClick={(e) => e.stopPropagation()} 
  >
    <Link to="/defence-overview" onClick={() => {
      setWhatDropdownOpen(false);
      setDefenceNestedOpen(false);
      handleMobileMenuClose();
    }}>
      Solutions
    </Link>

    <Link to="/defence-products" onClick={() => {
      setWhatDropdownOpen(false);
      setDefenceNestedOpen(false);
      handleMobileMenuClose();
    }}>
      Products
    </Link>

    <Link to="/defence-expertise" onClick={() => {
      setWhatDropdownOpen(false);
      setDefenceNestedOpen(false);
      handleMobileMenuClose();
    }}>
      Expertise
    </Link>

    <Link to="/defence-support" onClick={() => {
      setWhatDropdownOpen(false);
      setDefenceNestedOpen(false);
      handleMobileMenuClose();
    }}>
      Support
    </Link>
  </div>

</div>
</li>
            
            {/* GENAI LAB LINK */}
            <li>
              <Link
                to="/genai-lab"
                className={`genai-lab ${isActive("/genai-lab") ? "active" : ""} ${getLinkClass()} genai-lab `}
                onClick={handleMobileMenuClose}
              >
                GenAI Lab
              </Link>
            </li>

         
          </ul>

          {/* HAMBURGER MENU - MOBILE ONLY */}
          <button
            className={`hamburger-menu ${mobileMenuOpen ? "active" : ""} ${logoColor === "black" ? "hamburger-dark" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
            type="button"
          >
            <span />
            <span />
            <span />
          </button>

          {/* CTA BUTTON - DESKTOP ONLY */}
          <button
            className={`nav-cta-button ${logoColor === "black" ? "cta-dark" : ""}`}
            onClick={openStrategy}
            type="button"
            // onClick={() => {
            //   trackEvent("cta_click", {
            //     page: currentPage,
            //     cta: "Book Strategy Call",
            //     position: "Navbar",
            //   });
            //   openStrategy();

            // }}
          >

            Book a Strategy Call
          </button>
        </nav>
      </div>

      {/* FIXED BOTTOM CTA - MOBILE & TABLET ONLY */}
      <button
        className="mobile-bottom-cta"
        onClick={() => {
          trackEvent("cta_click", {
            page: currentPage,
            cta: "Book Strategy Call",
            position: "Mobile Bottom",
          });
          handleStrategyCallClick();
        }}
        type="button"
      >
        Book a Strategy Call
      </button>
    </>
  );
};

export default Navbar;